// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { classNames } from '@shared/ui-kit/utils/classnames';
import { isNullOrUndefined } from '@shared/utils';
import { IconButton } from '@shared/ui-kit/components/icon-button';
import { useRef, useState, useEffect, type HTMLAttributes, type FC } from 'react';
import { makeTriggerId, useTabsContext } from './tabs-context';
import styles from './tabs.module.css';

type ScrollableTabsProps = HTMLAttributes<HTMLDivElement>;

export const ScrollableTabs: FC<ScrollableTabsProps> = ({
  children,
  className,
  ...props
}) => {
  const context = useTabsContext();
  const tabsListRef = useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = useState({ left: false, right: false });

  const updateOverflowState = () => {
    if (tabsListRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = tabsListRef.current;
      setIsOverflowing({
        left: scrollLeft > 0,
        right: scrollLeft + clientWidth < scrollWidth,
      });
    }
  };

  const scroll = (direction: 'left' | 'right') => {
    if (tabsListRef.current) {
      const scrollAmount = direction === 'left' ? -250 : 250;
      tabsListRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    updateOverflowState();
    const handleResize = () => updateOverflowState();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (isNullOrUndefined(context.value)) {
      return;
    }
    const triggerId = makeTriggerId(context.baseId, context.value);
    const frameId = requestAnimationFrame(() => {
      const trigger = document.getElementById(triggerId);
      if (trigger && tabsListRef.current?.contains(trigger)) {
        trigger.scrollIntoView({
          inline: 'center',
          block: 'nearest',
          behavior: 'instant',
        });
        updateOverflowState();
      }
    });
    return () => cancelAnimationFrame(frameId);
  }, [context.baseId, context.value]);

  return (
    <div
      className={classNames(styles['tabs-scrollable'], className)}
      data-testid={`tablist-${context.baseId}`}
      {...props}
    >
      {isOverflowing.left && (
        <IconButton
          variant="icon"
          icon="ArrowLeft"
          className="!pr-2.5"
          size="xl"
          onClick={() => scroll('left')}
          data-testid="scroll-left"
        />
      )}
      <div
        ref={tabsListRef}
        data-testid="scrollable-tabs-container"
        className={`${styles['scrollbar-hide']} flex-1 overflow-x-auto whitespace-nowrap`}
        onScroll={updateOverflowState}
      >
        {children}
      </div>
      {isOverflowing.right && (
        <IconButton
          variant="icon"
          icon="ArrowRight"
          className="!pl-2.5"
          size="xl"
          onClick={() => scroll('right')}
          data-testid="scroll-right"
        />
      )}
    </div>
  );
};

ScrollableTabs.displayName = 'ScrollableTabs';
